/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    EpisodesApi,
    EpisodesGetRequest,
    SeasonsApi,
    SeasonsData,
    SeasonsGetRequest,
} from 'client-code/episode-guide';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IEpisodeGuideServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import { TEpisodeInfo, TEpisodeParams, TSeasonsParams, TServicesParams } from '../types';
import { UniqueIdentifier } from '../utils';

export class EpisodeGuideServiceRepositoryImpl implements IEpisodeGuideServiceRepository {
    private static TAG: string = 'EpisodeGuideServiceRepositoryImpl';

    private mParams: TServicesParams;

    private mCommonHeaders: CommonHeaders;

    private mConfiguration: Configuration;

    public constructor(params: TServicesParams) {
        this.mParams = params;

        this.mCommonHeaders = new CommonHeaders('SeriesScreen');

        const configurationParameters: ConfigurationParameters = {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders.headers,
            },
        };

        this.mConfiguration = new Configuration(configurationParameters);
    }

    private getCommonRequestParams() {
        return {
            applicationFeatureArea: this.mCommonHeaders.headers.ApplicationFeatureArea,
            applicationVersion: this.mCommonHeaders.headers.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            timeZoneName: this.mParams.timeZone,
            applicationName: this.mCommonHeaders.headers.ApplicationName,
            deviceType: this.mCommonHeaders.headers.DeviceType,
            productName: this.mCommonHeaders.headers.ProductName,
            msoName: this.mParams.msoName,
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    private async getSeasonsFromService(seasonsParams: TSeasonsParams): Promise<SeasonsData> {
        try {
            const { unifiedCollectionId, filter, sort, includeStickyInfo } = seasonsParams;

            const requestParameters: SeasonsGetRequest = {
                ...this.getCommonRequestParams(),
                unifiedCollectionId,
                filter,
                sort,
                includeStickyInfo,
            };

            const seasonsApi = new SeasonsApi(this.mConfiguration);
            const seasonsApiResponse = await seasonsApi.seasonsGet(requestParameters);
            return seasonsApiResponse.data;
        } catch (error: any) {
            Log.e(EpisodeGuideServiceRepositoryImpl.TAG, 'getSeasonsFromService()', error);
            throw error;
        }
    }

    private async getEpisodesFromService(episodeParams: TEpisodeParams): Promise<TEpisodeInfo> {
        try {
            const {
                unifiedCollectionId,
                filter,
                sort,
                partnerId,
                contentSupplierPartnerId,
                hideAdult,
                collapsedMixId,
                limit,
                offset,
                xToken,
                networkCallId,
            } = episodeParams;

            const requestParameters: EpisodesGetRequest = {
                ...this.getCommonRequestParams(),
                unifiedCollectionId,
                filter,
                sort,
                partnerId,
                contentSupplierPartnerId,
                hideAdult,
                collapsedMixId,
                ...(limit && { limit }),
                ...(offset && { offset }),
                xToken,
            };

            const episodeApi = new EpisodesApi(this.mConfiguration);
            const episodesApiResponse = await episodeApi.episodesGet(requestParameters);
            return { episodeInfo: episodesApiResponse.data, networkCallId };
        } catch (error: any) {
            Log.e(EpisodeGuideServiceRepositoryImpl.TAG, 'getEpisodesFromService()', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchSeasons(seasonsParams: TSeasonsParams): Promise<SeasonsData> {
        try {
            return await this.getSeasonsFromService(seasonsParams);
        } catch (error) {
            Log.e(EpisodeGuideServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchEpisodes(episodeParams: TEpisodeParams): Promise<TEpisodeInfo> {
        try {
            return await this.getEpisodesFromService(episodeParams);
        } catch (error) {
            Log.e(EpisodeGuideServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }
}
